import {
  ClinicalImpressionCode,
  getReferenceId,
} from '@quromedical/fhir-common';
import type { Note, NoteBundle, PartialTaskWithId } from '@quromedical/models';
import { last } from '@quromedical/utils';
import type { NoteWithTask } from 'core/notes';
import { getNextTokenFromLink } from 'hooks/usePaginatedData';
import { useRevalidation } from 'hooks/useRevalidation';
import { NotesApi } from 'integration/aggregate';
import { useCallback } from 'react';
import useSWR from 'swr';
import useSWRInfinite from 'swr/infinite';

const noteApi = new NotesApi();

interface UseAllNotesHookResult {
  revalidate: () => Promise<void>;
  loadMore: () => void;
  canLoadMore: boolean;
  data?: NoteWithTask[];
  pins?: Note[];
  error: unknown;
  isLoading: boolean;
}

export const useAllNotes = (
  id: string,
  search: string,
  tasks: PartialTaskWithId[],
): UseAllNotesHookResult => {
  const trimmedSearch = search.trim();

  const key = `${id}-note-list-${trimmedSearch}`;

  /**
   * SWR needs a key for any case where they is data including the first load of data.
   */
  const getNextTokenFromData = (page?: number, data?: NoteBundle | null) => {
    if (page === 0) {
      return key;
    }
    const nextToken = getNextTokenFromLink(data?.link);
    if (!nextToken) {
      return null;
    }

    return `${key}${nextToken}`;
  };

  const codes = new Set(Object.values(ClinicalImpressionCode));
  codes.delete(ClinicalImpressionCode.DailySummary);
  codes.delete(ClinicalImpressionCode.OverallSummary);

  const fetcher = async (pageToken?: string) =>
    noteApi.getNotes({
      pageToken: pageToken?.replace(key, ''),
      count: 20,
      code: Array.from(codes).join(','),
      search: trimmedSearch || undefined,
      subject: id,
    });

  const {
    error,
    data: listData = [],
    isValidating,
    setSize,
    size,
  } = useSWRInfinite<NoteBundle>(getNextTokenFromData, fetcher);

  const data = listData.map((data) => data?.notes).flat();
  const revalidate = useRevalidation(key);

  // the data shouldn't be re-fetched since it's the same key between the hooks
  const { data: pinsBundle } = useSWR<NoteBundle>(key, () => fetcher());

  const notesWithTasks = data?.map<NoteWithTask>((note) => ({
    ...note,
    task: tasks.find((task) => getReferenceId(task.focus) === note.id),
  }));

  const hasNext = !!getNextTokenFromLink(last(listData)?.link);

  const loadNext = useCallback(() => {
    setSize(size + 1);
  }, [setSize, size]);

  return {
    isLoading: isValidating,
    data: notesWithTasks,
    pins: pinsBundle?.pins,
    error,
    revalidate,
    canLoadMore: hasNext,
    loadMore: loadNext,
  };
};
